import { referralApi } from './referralApi';
import { normalizeReferralCode } from './referralCode';
import { getOrCreateReferralIdentifier } from './referralIdentifier';
import {
  IGetReferrerResponse,
  ILinkReferralAccount,
  IReferralLookupResponse,
} from './referralTypes';

async function linkReferralAccount(referralLink: string): Promise<ILinkReferralAccount> {
  const referralAPI = await referralApi.linkReferralAccount(referralLink);

  return referralAPI;
}

async function getReferrer(): Promise<IGetReferrerResponse> {
  const response = await referralApi.getReferrer();

  return response;
}

/**
 * Codes already claimed on this page load.
 *
 * GOTU-7244 asks the client to "only claim when no identifier is stored yet",
 * but the identifier is minted once and persisted — so that condition is false
 * from the first visit on and the guard could never fire. The guard that works
 * is per-arrival: has a claim already been written for *this code* on *this
 * page load*. A remount or back-navigation with `?referral=` still in the URL
 * hits the guard; a reload is a new page load and the server's same-day
 * `(identifier, code)` window is the backstop. A genuine second click days
 * later still writes a row, which is the repeat-click signal the table measures.
 */
const claimedCodes = new Set<string>();

/** Test seam — production has one arrival per page load. */
function resetClaimGuard(): void {
  claimedCodes.clear();
}

/**
 * Records a click. **Measurement only** — nothing reads a claim back, and
 * attribution runs off `referredByCode` on the registration event instead.
 *
 * Deliberately fire-and-forget: a 5xx, a timeout or an offline browser changes
 * nothing downstream, so there is nothing to retry and nothing to surface, and
 * a failed claim must never block signup (GOTU-7244).
 */
async function recordReferralClaim(code: string): Promise<void> {
  const normalized = normalizeReferralCode(code);
  // Only emptiness is held back. Per GOTU-7141 a miss is data: the server
  // assigns `invalid_code` and the row is still the denominator.
  if (!normalized) return;
  if (claimedCodes.has(normalized)) return;

  claimedCodes.add(normalized);

  try {
    await referralApi.postReferralClaim({
      code: normalized,
      identifier: getOrCreateReferralIdentifier(),
      platform: 'web',
    });
  } catch {
    // Swallowed by design: a failed claim must never block signup, lose the
    // code, or throw into the landing path.
  }
}

/**
 * Confirms who a code belongs to, before an account exists.
 *
 * A failure degrades to `server_error` rather than throwing, so a 5xx leaves
 * signup usable with the referrer name simply absent. Neither `server_error`
 * nor the server's own `unavailable` may ever be rendered as "invalid code".
 *
 * The two are reported apart on purpose (GOTU-8354): `unavailable` is the
 * enumeration breaker declining to confirm — the server answering, correctly —
 * while `server_error` is no answer at all. Collapsing them hid outages behind
 * a status that is meant to be routine. Callers still treat both the same way.
 */
async function lookupReferrerByCode(code: string): Promise<IReferralLookupResponse> {
  try {
    return await referralApi.getReferrerByCode(normalizeReferralCode(code));
  } catch {
    return { status: 'server_error' };
  }
}

export const referralService = {
  getReferrer,
  linkReferralAccount,
  lookupReferrerByCode,
  recordReferralClaim,
  resetClaimGuard,
};
